import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Tbody, Td, Th, Thead, Tr } from '../../../components/Table';
import styled from 'styled-components/macro';

import { colors, distances, request_states } from '../../../styles/constants';
import { H2, H3, H4 } from '../../../components/Typography';
import { RequestLog as RequestLogType } from '../../../types/audit/generated';
import { DateTime } from '../../../components/Formatters';
import Icon from '../../../components/Icons';
import { Dd, Dl, Dt } from '../../../components/DescriptionList/Vertical';
import { CHECKOUT_API_HOSTNAME, CORE_API_HOSTNAME } from '../../../env';
import { Transaction } from '../../../types/checkout';
import Hr from '../../../components/Hr';
export interface RequestLogsProps  {
    requestLogs: RequestLogType[];
    transaction: Transaction;
}

const Caret = (
    {
        isExpanded,
    }: {
        isExpanded: boolean;
    }
) => {
    return (
        <Icon
            icon="caret_down"
            fill={colors.text}
            className={['clickable', isExpanded && 'flip'].filter((x) => x).join(' ')}
        />
    );
};

const objectKeysToLowerCase = (input: { [key: string]: any } | undefined) => {
    if (!input) {
        return input;
    }
    return Object.keys(input).reduce((newObj, key) => {
        newObj[key.toLowerCase()] = input[key];
        return newObj;
    }, {} as { [key: string]: any });
};

interface HeaderAndBodyProps {
    body?: any;
    headers?: {
        [key: string]: any;
    };
    field: 'request' | 'response';
}

const prettyPrintJson = (input: any): string => {
    try {
        const jsonObject = JSON.parse(input);
        return JSON.stringify(jsonObject, null, 2);
    } catch (error) {
        return input;
    }
};

const HeaderAndBody = (
    {
        body,
        headers,
        field,
    }: HeaderAndBodyProps
) => {
    const [isExpanded, toggle] = useState(false);
    const lowerCaseHeaders = objectKeysToLowerCase(headers);
    const contentType = (lowerCaseHeaders && lowerCaseHeaders['content-type']) || 'text/plain';
    const { t } = useTranslation();
    return (
        <>
            <LogBodyHeader onClick={() => toggle(!isExpanded)}>
                {t(`payments.transaction.requests.fields.${field}_details`)} <Caret isExpanded={isExpanded} />
            </LogBodyHeader>
            {isExpanded && (
                <div>
                    <H4>{t('payments.transaction.requests.fields.headers')}</H4>
                    <BodyPre contentType={'application/json'} body={JSON.stringify(lowerCaseHeaders)} />
                    {body && (
                        <>
                            <H4>{t('payments.transaction.requests.fields.body')}</H4>
                            <BodyPre contentType={contentType} body={prettyPrintJson(body)} />
                        </>
                    )}
                </div>
            )}
        </>
    );
};

interface BodyPreProps {
    contentType: string;
    body?: any;
}

const mendBody = (body: any) => {
    // Work-around to handle a serverside bug where we get a body with invalid
    // json containing some trailing characters that should not have been
    // included in the body.
    if (typeof body === 'string' && body.endsWith('---8<--')) {
        // remove the trailing ---8<--
        return body.slice(0, -7);
    }
    return body;
};

const BodyPre = (
    {
        contentType,
        body,
    }: BodyPreProps
) => {
    if (!body) {
        return null;
    }
    let displayBody = body;
    try {
        if (contentType === 'application/json') {
            displayBody = JSON.stringify(JSON.parse(mendBody(body)), undefined, 2);
        }
    } catch {
        // ignore errors silently, use initial displayBody value
    }
    return <Pre>{displayBody}</Pre>;
};

interface ItemProps {
    log: RequestLogType;
    transaction: Transaction;
}

const destinationColumnValue = (log: RequestLogType, transaction: Transaction) => {
    const requestUrl = log.request?.url || '';
    if (requestUrl.includes(CHECKOUT_API_HOSTNAME) || requestUrl.includes(CORE_API_HOSTNAME)) {
        const subpath = requestUrl
            .replace(CHECKOUT_API_HOSTNAME, '')
            .replace(CORE_API_HOSTNAME, '');
        if (subpath.includes(`/v1/transactions/${transaction.id}/capture`)) {
            return `.../capture`;
        }
        if (subpath.includes(`/v1/transactions/${transaction.id}/refund`)) {
            return `.../refund`;
        }
        if (subpath.includes(`/v1/transactions/${transaction.id}/void`)) {
            return `.../void`;
        }
        return subpath;
    }
    try {
        return new URL(requestUrl).hostname + '...';
    } catch (err) {
        // invalid request.url may be the cause for the error
    }
    return '...';
};

const Item = (
    {
        log,
        transaction,
    }: ItemProps
) => {
    const [isExpanded, toggle] = useState(false);
    const { t } = useTranslation();
    const destination = destinationColumnValue(log, transaction);

    const responseTime = log.response_time || 0;
    const responseTimeMs = `${responseTime.toFixed(0)} ms`;
    const statusDot = <Dot status={log.success ? 'OK' : 'FAILED'} />;
    const createdAt = <DateTime isoString={log.created_at || ''} boldTime />;
    return (
        <>
            <HoverTr onClick={() => toggle(!isExpanded)}>
                <Td>{createdAt}</Td>
                <Td>{log.request?.method}</Td>
                <Td className="collapse-1200 ellipsis">{destination}</Td>
                <Td>
                    {log.response?.status} {statusDot}
                </Td>
                <Td>
                    {responseTimeMs} {responseTime > 1000 && <Icon icon={'snail'} fill={colors.warning} />}
                </Td>
                <Td>
                    <Caret isExpanded={isExpanded} />
                </Td>
            </HoverTr>
            {isExpanded && (
                <Tr className="no-highlight alt">
                    <Td colSpan={6}>
                        <Padded>
                            <Dl>
                                <Dt>{t('payments.transaction.requests.fields.date')}</Dt>
                                <Dd>{createdAt}</Dd>
                            </Dl>
                            <Dl>
                                <Dt>{t('payments.transaction.requests.fields.method')}</Dt>
                                <Dd>{log.request?.method}</Dd>
                            </Dl>
                            <Dl>
                                <Dt>{t('payments.transaction.requests.fields.url')}</Dt>
                                <Dd className="word-break-all">{log.request?.url}</Dd>
                            </Dl>
                            <Dl>
                                <Dt>{t('payments.transaction.requests.fields.status')}</Dt>
                                <Dd>
                                    {log.response?.status} {statusDot}
                                </Dd>
                            </Dl>
                            <Dl>
                                <Dt>{t('payments.transaction.requests.fields.response_time')}</Dt>
                                <Dd>
                                    {responseTimeMs}{' '}
                                    {responseTime > 1000 && <Icon icon={'snail'} fill={colors.warning} />}
                                </Dd>
                            </Dl>
                            {(log.request?.body || log.request?.headers) && (
                                <HeaderAndBody
                                    body={log.request.body}
                                    headers={log.request.headers}
                                    field={'request'}
                                />
                            )}
                            {(log.response?.body || log.response?.headers) && (
                                <HeaderAndBody
                                    body={log.response.body}
                                    headers={log.response.headers}
                                    field={'response'}
                                />
                            )}
                        </Padded>
                    </Td>
                </Tr>
            )}
        </>
    );
};

const RequestLog = ({ requestLogs, transaction }: RequestLogsProps) => {
    const { t } = useTranslation();
    return (
        <Wrapper>
            <H2>{t('payments.transaction.logs')}</H2>
            <Hr />
            <Spacer />
            <Table className="no-wrap">
                <Thead>
                    <Tr>
                        <Th>{t('payments.transaction.requests.fields.date')}</Th>
                        <Th>{t('payments.transaction.requests.fields.method')}</Th>
                        <Th className="collapse-1200">{t('payments.transaction.requests.fields.destination')}</Th>
                        <Th>{t('payments.transaction.requests.fields.status')}</Th>
                        <Th>{t('payments.transaction.requests.fields.response_time')}</Th>
                        <Th />
                    </Tr>
                </Thead>
                <Tbody>
                    {requestLogs?.map((log, i) => (
                        <Item key={log?.id || i} log={log} transaction={transaction} />
                    ))}
                </Tbody>
            </Table>
        </Wrapper>
    );
};

export default RequestLog;

const HoverTr = styled(Tr)`
    cursor: pointer;
`;
const Wrapper = styled.div`
    padding-top: ${distances.normal};
    overflow: hidden;
`;

const Spacer = styled.div`
    height: ${distances.normal};
`;

const LogBodyHeader = styled(H3)`
    margin-top: ${distances.normal};
    cursor: pointer;
`;

const Pre = styled.pre`
    background-color: #f8f8f8;
    padding: ${distances.small} ${distances.small};
    margin: ${distances.small} 0px;
    white-space: pre-wrap;
    word-break: break-all;
`;

interface DotProps {
    status: 'OK' | 'FAILED';
}

const Dot = styled.span<DotProps>`
    height: 10px;
    width: 10px;
    background-color: ${(props) => request_states[props.status] || 'transparent'};
    border-radius: 50%;
    display: inline-block;
`;

const Padded = styled.div`
    padding-left: ${distances.small};
    padding-right: ${distances.tiny};
`;
